import React from "react";
//#region redux
import { AppState } from "store";
import { useSelector } from "react-redux";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { User } from "models/propTypes/userProfilePropTypes";
import { Amount } from "models/utilTypes/currencyType";
import { CurrencyEnums, TableEnums } from "models/enums";
import { DateConstants } from "utils/constants";
import { WintSuperkollAPIModel as ApiModels, WintUtilsWebApi } from "wint-api-client/dist/types";
import { PersonSalaryFormTypes } from "models/formTypes";
//#endregion
//#region components
import { TableAsync, useFilterContext } from "components/common";
import PersonSalaryStatusTags from "./statusTags";
import { useSalaryService } from "serviceHooks";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const PersonSalaryListView: React.FC = (props) => {
  const salaryService = useSalaryService();
  const { filter } = useFilterContext<PersonSalaryFormTypes.Filter>();

  const filterFunc = React.useMemo(() => {
    return filter ? (baseFilter: WintUtilsWebApi.Filter) => salaryService.filterPersonSalaries(baseFilter, filter) : [];
  }, [filter]);

  const onRowClicked = (row: ApiModels.PersonSalaryEntry) => {
    salaryService.downloadSalarySpecification(row.id);
  };

  const superkollSalaryType = useSelector((state: AppState) => state.auth.selectedCompany.superkollSalaryType);
  const createSalaryAutoFromSalaryPersonEntry = useSelector(
    (state: AppState) => state.auth.selectedCompany.createSalaryAutoFromSalaryPersonEntry
  );

  const salaryNotInApprovalState = (salary: ApiModels.PersonSalaryEntry) =>
    salary.state !== ApiModels.PersonSalaryState.Waiting &&
    salary.state !== ApiModels.PersonSalaryState.AwaitingAutoInclusion;

  return (
    <TableAsync<ApiModels.PersonSalaryEntry>
      tableId={TableEnums.TableType.PersonSalaryList}
      properties={{
        personName: {
          description: TranslationKeys.Common.Employee,
          formatter: (salary: ApiModels.PersonSalaryEntry) => new User(salary.personId, salary.personName),
          maxWidth: 150,
          noWrap: true,
        },
        monthDb: {
          description: TranslationKeys.Common.RefersTo,
          formatter: (salary: ApiModels.PersonSalaryEntry) =>
            LocalizationHelperInstance.getFormattedDate(
              DateHelper.getDateFromYearMonth(salary.monthDb),
              DateConstants.MonthYearShortConfig
            ),
          noWrap: true,
          maxWidth: 100,
          classification: "date",
        },
        payoutDate: {
          description: TranslationKeys.Common.PaymentDate,
          formatter: (salary: ApiModels.PersonSalaryEntry) => (salary.payoutDate ? new Date(salary.payoutDate) : ""),
        },
        state: {
          description: TranslationKeys.Common.Status,
          formatter: (salary: ApiModels.PersonSalaryEntry) => <PersonSalaryStatusTags state={salary.viewState} />,
          noWrap: true,
        },
        taxesAndFees: {
          description: TranslationKeys.Salary.SocialFees,
          formatter: (salary: ApiModels.PersonSalaryEntry) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, salary.taxesAndFees),
          alignment: "right",
          classification: "amount",
        },
        benefitAmount: {
          description: TranslationKeys.Salary.Benefits,
          formatter: (salary: ApiModels.PersonSalaryEntry) =>
            salaryNotInApprovalState(salary) ? new Amount(CurrencyEnums.CurrencyCode.SEK, salary.benefitAmount) : "",
          alignment: "right",
        },
        benefitNetDeduction: {
          description: TranslationKeys.Salary.BenefitNetDeduction,
          formatter: (salary: ApiModels.PersonSalaryEntry) =>
            salaryNotInApprovalState(salary)
              ? new Amount(CurrencyEnums.CurrencyCode.SEK, salary.benefitNetDeduction)
              : "",
          alignment: "right",
          classification: "amount",
        },
        benefitGrossSalaryIncrease: {
          description: TranslationKeys.Salary.BenefitGrossSalaryIncrease,
          formatter: (salary: ApiModels.PersonSalaryEntry) =>
            salaryNotInApprovalState(salary)
              ? new Amount(CurrencyEnums.CurrencyCode.SEK, salary.benefitGrossSalaryIncrease)
              : "",
          alignment: "right",
          classification: "amount",
        },
        grossAmount: {
          description: TranslationKeys.Salary.TaxableAmount,
          formatter: (salary: ApiModels.PersonSalaryEntry) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, salary.grossAmount),
          alignment: "right",
          className: cssExports.textBlack,
        },
        monthlySalary: {
          description: TranslationKeys.Salary.MonthlySalary,
          formatter: (salary: ApiModels.PersonSalaryEntry) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, salary.monthlySalary),
          alignment: "right",
          className: cssExports.textBlack,
        },
        group: {
          formatter: (salary: ApiModels.PersonSalaryEntry) => {
            if (salary.isSalaryActive)
              return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Active2);
            else return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.InActive2);
          },
        },
      }}
      rowClickHandler={onRowClicked}
      filter={filterFunc}
      maxRowCount={10}
      pagination="advanced"
      orderByProperty="isSalaryActive"
      orderByDescending
      thenByProperty="monthDb"
      thenByDescending
      hiddenColumns={
        superkollSalaryType === ApiModels.SuperkollSalaryType.Simple && createSalaryAutoFromSalaryPersonEntry
          ? ["grossAmount"]
          : superkollSalaryType === ApiModels.SuperkollSalaryType.Simple && !createSalaryAutoFromSalaryPersonEntry
            ? ["taxesAndFees", "benefitAmount", "benefitNetDeduction", "benefitGrossSalaryIncrease", "grossAmount"]
            : ["monthlySalary"]
      }
    />
  );
};

export default PersonSalaryListView;
